import { useCallback } from "react";
import GridSvg from "@assets/design/wizard-error-grid.svg?react";
import GridUploadSvg from "@assets/design/wizard-error-grid-upload.svg?react";
import GridProcessSvg from "@assets/design/wizard-error-grid-process.svg?react";
import GridRegisterSvg from "@assets/design/wizard-error-grid-register.svg?react";
import GridPublishSvg from "@assets/design/wizard-error-grid-publish.svg?react";
import { FaroButton } from "@components/common/faro-button";
import { FaroTextButton } from "@components/common/faro-text-button";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { BaseProjectProps } from "@custom-types/project-types";
import { assert } from "@faro-lotv/foundation";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Stack } from "@mui/material";
import { InspectButton } from "@pages/data-management/buttons/inspect-button";
import { ContinueWithProcessedScansButton } from "@pages/data-management/buttons/continue-with-processed-scans-button";
import { RetryPublishButton } from "@pages/data-management/buttons/retry-publish-button";
import { RetryUploadButton } from "@pages/data-management/buttons/retry-upload-button";
import { isWorkflowStateError, TableItem, WorkflowState } from "@pages/data-management/data-management-types";
import { CancelImportButton } from "@pages/data-management/import-data/cancel-import-button";
import { RegisterDataButton } from "@pages/data-management/register-data-button";
import { setHasUnacknowledgedError } from "@store/ui/ui-slice";
import { useAppDispatch } from "@store/store-helper";
import { sphereColors } from "@styles/common-colors";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { DataManagementEvents } from "@utils/track-event/track-event-list";

interface Props extends BaseProjectProps {
  /** Main state of the Data Management workflow. See DataManagementWorkflow. */
  state: WorkflowState;
  /** ID of the registration revision. */
  registrationId?: string;
  /** Array of DataManagementTableItem entities. */
  items: TableItem[];
  /** Sets the flag whether the user has closed the error view. In this case, the table view is shown. */
  setHasClosedErrorView: React.Dispatch<React.SetStateAction<boolean>>;
  /** Callback to set whether to prevent the redirect to the Viewer when the published state is reached. */
  setShouldPreventRedirect: React.Dispatch<React.SetStateAction<boolean>>;
  /** boolean indicating if we have unsupported scans */
  hasUnsupportedScans: boolean;
}

/** The error view in Data Management consisting of a title, description and action buttons. */
export function ErrorView({
  project,
  state,
  registrationId,
  items,
  setHasClosedErrorView,
  setShouldPreventRedirect,
  hasUnsupportedScans = false,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();
  const { isExtraSmall } = useMediaQueryList();

  let title: string;
  let desc: string;
  let shouldShowClose = false;
  let shouldShowReload = false;
  let shouldShowCancelUpload = false;
  let shouldShowRetryUpload = false;
  let shouldShowContinueProcessing = false;
  let shouldShowAdjustRegistration = false;
  let shouldShowRetryRegistration = false;
  let shouldShowRetryPublish = false;
  let grid: JSX.Element;

  assert(isWorkflowStateError(state), `ErrorView: Unexpected state: ${state} - Expected an error state.`);

  switch (state) {
    case "startError":
      title = "Fetching Data Failed";
      desc = "Failed to fetch the data for this project. Please reload the page to try again.";
      shouldShowReload = true;
      grid = <GridSvg/>;
      break;
    case "uploadError":
      title = "Upload Failed";
      desc = "An error occured, preventing some scans from being uploaded. " +
        "You can retry the upload or close this dialog to cancel the current upload process.";
      shouldShowCancelUpload = true;
      shouldShowRetryUpload = true;
      grid = <GridUploadSvg/>;
      break;
    case "processError": {
      title = "Processing Failed";
      shouldShowClose = true;
      shouldShowContinueProcessing = !hasUnsupportedScans;
      const nextStep = shouldShowContinueProcessing ?
          "You can continue with the processed scans or close this dialog to review the failed ones before deciding." :
          "Close this dialog to review the failed scans.";
      desc = `Some scans couldn’t be processed and must be removed from your project. ${nextStep}`;
      grid = <GridProcessSvg/>;
      break;
    }
    case "registerBadResult":
      assert(registrationId, "Registration ID should be known at this point.");
      title = "Registration Failed";
      desc = "Your scans weren’t automatically registered. " +
        "Check each scan position and adjust them manually in the registration editor before publishing.";
      shouldShowClose = true;
      shouldShowAdjustRegistration = true;
      grid = <GridRegisterSvg/>;
      break;
    case "registerError":
      title = "Registration Failed";
      desc = "An error occurred and we couldn’t complete your registration. Please try again.";
      shouldShowClose = true;
      shouldShowRetryRegistration = true;
      grid = <GridRegisterSvg/>;
      break;
    case "publishError":
      assert(registrationId, "Registration ID should be known at this point.");
      title = "Publishing Failed";
      desc = "We couldn't make your data available in the Sphere XG Viewer. Your changes weren't published. " +
        "Please try again in a few moments, or contact support if the issue persists.";
      shouldShowClose = true;
      shouldShowRetryPublish = true;
      grid = <GridPublishSvg/>;
      break;
  }

  const resetUnacknowledgedError = useCallback(() => {
    dispatch(setHasUnacknowledgedError(false));
  }, [dispatch]);

  const onReload = useCallback((): void => {
    trackEvent({ name: DataManagementEvents.reloadInitial });
    dispatch(setHasUnacknowledgedError(false));
    window.location.reload();
  }, [dispatch, trackEvent]);

  const onClose = useCallback((): void => {
    trackEvent({ name: DataManagementEvents.closeError });
    dispatch(setHasUnacknowledgedError(false));
    setHasClosedErrorView(true);
  }, [dispatch, setHasClosedErrorView, trackEvent]);

  let height;
  if (isExtraSmall && state === "startError") {
    height = "calc(100vh - 300px)";
  } else if (isExtraSmall) {
    height = "calc(100vh - 320px)";
  } else if (state === "startError") {
    height = "calc(100vh - 200px)";
  } else {
    height = "calc(100vh - 220px)";
  }

  return (
    <Stack sx={{
      width: "100%",
      height,
      minHeight: isExtraSmall ? "420px" : "520px",
      marginTop: state === "startError" ? "0px" : "30px",
      padding: "30px",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: sphereColors.pureWhite,
      border: `1px solid ${sphereColors.gray200}`,
    }}>
      {/* Grid image */}
      {!isExtraSmall && (
        <Box
          data-testid="sa-ev-grid"
          sx={{
            marginBottom: "30px",
            textAlign: "center",
            overflowX: "clip",
          }}
        >
          {grid}
        </Box>
      )}

      {/* Title */}
      <Box
        data-testid="sa-ev-title"
        sx={{
          width: "100%",
          maxWidth: "640px",
          marginBottom: "20px",
          textAlign: "center",
          fontSize: "32px",
          fontWeight: "600",
        }}
      >
        {title}
      </Box>

      {/* Description */}
      <Box
        data-testid="sa-ev-subtitle"
        sx={{
          width: "100%",
          maxWidth: "640px",
          marginBottom: "40px",
          textAlign: "center",
          fontSize: "16px",
        }}
      >
        {desc}
      </Box>

      {/* Buttons */}
      <Stack direction={isExtraSmall ? "column" : "row"}
        gap="15px"
        sx={{
          width: "100%",
          marginBottom: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Close button */}
        { shouldShowClose &&
          <SphereTooltip title="Close">
            <FaroTextButton
              dataTestId="sa-ev-button-close"
              onClick={onClose}
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                backgroundColor: sphereColors.gray50,
                paddingLeft: "25px",
                paddingRight: "25px",
              }}
            >
              Close
            </FaroTextButton>
          </SphereTooltip>
        }

        {/* Reload button for fetching error */}
        { shouldShowReload &&
          <SphereTooltip title="Reload the Page">
            <FaroButton
              dataTestId="sa-button-reload"
              onClick={onReload}
            >
              Reload
            </FaroButton>
          </SphereTooltip>
        }

        {/* Cancel Import button, with "Close" as caption, for uploading error */}
        { shouldShowCancelUpload &&
          <CancelImportButton
            projectId={project.id}
            state={state}
            isFromErrorView={true}
            setShouldPreventRedirect={setShouldPreventRedirect}
            onClickBefore={resetUnacknowledgedError}
          />
        }

        {/* Retry button for uploading error */}
        { shouldShowRetryUpload &&
          <RetryUploadButton
            project={project}
            caption="Retry"
            onClickBefore={resetUnacknowledgedError}
          />
        }

        {/* Continue button for processing error */}
        { shouldShowContinueProcessing &&
          <ContinueWithProcessedScansButton
            projectId={project.id}
            caption="Continue"
            items={items}
            onClickBefore={resetUnacknowledgedError}
          />
        }

        {/* Adjust Registration button for bad registration result */}
        { shouldShowAdjustRegistration &&
          <InspectButton
            projectId={project.id}
            state={state}
            isFromErrorView={true}
            isDevMode={false}
            hasBadRegistration={true}
            registrationId={registrationId}
            onClickBefore={resetUnacknowledgedError}
          />
        }

        {/* Retry button for registration system error */}
        { shouldShowRetryRegistration &&
          <RegisterDataButton
            state={state}
            caption="Retry"
            isDevMode={false}
            onClickBefore={resetUnacknowledgedError}
          />
        }

        {/* Retry button for publishing error */}
        { shouldShowRetryPublish &&
          <RetryPublishButton
            projectId={project.id}
            caption="Retry"
            registrationId={registrationId}
            onClickBefore={resetUnacknowledgedError}
          />
        }
      </Stack>
    </Stack>
  );
}
